import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import Icon from '../../components/AppIcon';
import OnboardingModal from '../../components/ui/OnboardingModal';
import { getUserContent } from '../../services/contentService';
import { getDashboardMetrics, fetchAnalyticsData } from '../../services/analyticsService';
import { listScheduledPosts, cancelScheduledPost } from '../../services/scheduleService';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../components/ui/Toast';

const PLATFORM_TABS = [
  { key: 'linkedin', label: 'LinkedIn', icon: 'Linkedin', color: '#0A66C2' },
  { key: 'twitter', label: 'X', icon: 'X', color: '#A78BFA' },
];

// Donut chart metric card
const DonutMetric = ({ title, value, change, changeType, color, icon }) => {
  const numericVal = parseInt(String(value).replace(/[^0-9]/g, '')) || 0;
  const maxVal = Math.max(numericVal * 1.5, 100);
  const pct = maxVal > 0 ? (numericVal / maxVal) * 100 : 0;
  const data = [
    { value: pct },
    { value: 100 - pct },
  ];

  return (
    <div className="bg-card border border-border rounded-xl p-5 flex items-center gap-4">
      <div className="w-16 h-16 flex-shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} innerRadius={20} outerRadius={30} startAngle={90} endAngle={-270} dataKey="value" stroke="none">
              <Cell fill={color} />
              <Cell fill="var(--color-muted)" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-muted-foreground mb-0.5">{title}</p>
        <p className="text-xl font-bold text-foreground">{value}</p>
        {change && (
          <span className={`text-xs font-medium ${
            changeType === 'positive' ? 'text-emerald-500' : changeType === 'negative' ? 'text-red-400' : 'text-muted-foreground'
          }`}>
            {change}
          </span>
        )}
      </div>
    </div>
  );
};

// Post row
const PostRow = ({ post, onClick }) => {
  const isLinkedIn = post.platform === 'linkedin';
  return (
    <button
      onClick={() => onClick(post.id)}
      className="w-full flex items-center gap-4 p-4 bg-card border border-border rounded-xl hover:border-muted-foreground transition-all text-left group"
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${
        isLinkedIn ? 'bg-[#0A66C2]/10' : 'bg-muted'
      }`}>
        <Icon name={isLinkedIn ? 'Linkedin' : 'X'} size={16} color={isLinkedIn ? '#0A66C2' : undefined} className={!isLinkedIn ? 'text-foreground' : ''} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-foreground truncate group-hover:text-primary transition-colors">
          {post.content?.substring(0, 80) || 'Untitled post'}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5">{post.publishedAt}</p>
      </div>
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1"><Icon name="Heart" size={12} /> {post.likes || 0}</span>
        <span className="flex items-center gap-1"><Icon name="MessageCircle" size={12} /> {post.comments || 0}</span>
        <Icon name="ChevronRight" size={14} className="text-muted-foreground group-hover:text-foreground" />
      </div>
    </button>
  );
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();
  const [recentPosts, setRecentPosts] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activePlatform, setActivePlatform] = useState('linkedin');
  const [currentPage, setCurrentPage] = useState(1);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const toast = useToast();
  const postsPerPage = 5;

  // Greeting
  const greeting = useMemo(() => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 18) return 'Good Afternoon';
    return 'Good Evening';
  }, []);

  // Show onboarding if:
  // - No social accounts linked AND
  // - Not dismissed in this login session AND
  // - At least one account is still unlinked
  // Dismissal is stored per-session (sessionStorage) — resets on next login.
  // Once any account is linked, the modal never shows again (even without dismissing).
  useEffect(() => {
    if (!authLoading && userProfile) {
      const hasLinkedIn = !!userProfile.linkedin_access_token;
      const hasTwitter = !!userProfile.twitter_access_token;
      const dismissed = sessionStorage.getItem('onboarding_dismissed');

      if (!hasLinkedIn && !hasTwitter && !dismissed) {
        setShowOnboarding(true);
      } else {
        setShowOnboarding(false);
      }
    }
  }, [authLoading, userProfile]);

  const handleOnboardingSkip = () => {
    setShowOnboarding(false);
    sessionStorage.setItem('onboarding_dismissed', 'true');
  };

  useEffect(() => {
    if (!authLoading && user) loadDashboardData();
  }, [user, authLoading]);

  const loadDashboardData = async () => {
    try {
      const [posts, metricsData] = await Promise.all([
        getUserContent(),
        loadAnalyticsMetrics(),
      ]);
      setRecentPosts(posts || []);
      if (metricsData) setMetrics(metricsData);
      // Load scheduled posts separately (non-blocking)
      loadScheduledPosts();
    } catch {
      setRecentPosts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadScheduledPosts = async () => {
    try {
      const result = await listScheduledPosts('scheduled');
      setScheduledPosts(result?.data || []);
    } catch {
      // Silently fail — widget just won't show
    }
  };

  const handleCancelScheduled = async (postId) => {
    try {
      await cancelScheduledPost(postId);
      setScheduledPosts(prev => prev.filter(p => p.id !== postId));
      toast.success('Scheduled post cancelled.');
    } catch (error) {
      toast.error('Failed to cancel. Please try again.');
    }
  };

  const loadAnalyticsMetrics = async () => {
    try {
      await fetchAnalyticsData(false);
      return await getDashboardMetrics();
    } catch { return null; }
  };

  const handleViewPost = (postId) => {
    const actualId = postId.split('_')[0];
    navigate(`/post/${actualId}`);
  };

  // Filtered + paginated posts
  const filteredPosts = useMemo(() => {
    return recentPosts.filter(p => p.platform === activePlatform);
  }, [recentPosts, activePlatform]);

  const totalPages = Math.ceil(filteredPosts.length / postsPerPage);
  const paginatedPosts = filteredPosts.slice((currentPage - 1) * postsPerPage, currentPage * postsPerPage);

  // Reset page when platform changes
  useEffect(() => { setCurrentPage(1); }, [activePlatform]);

  // Posts per day for bar chart (last 7 days)
  const postsByDay = useMemo(() => {
    const days = [];
    for (let i = 6; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toLocaleDateString('en-US', { weekday: 'short' });
      const dayStart = new Date(d.setHours(0, 0, 0, 0));
      const dayEnd = new Date(d.setHours(23, 59, 59, 999));
      const count = recentPosts.filter(p => {
        const created = new Date(p.createdAt);
        return created >= dayStart && created <= dayEnd;
      }).length;
      days.push({ day: key, posts: count });
    }
    return days;
  }, [recentPosts]);

  const displayName = userProfile?.full_name || user?.email?.split('@')?.[0] || 'User';

  return (
    <div className="min-h-screen bg-background">
      {/* Onboarding Modal — shown when no social accounts linked */}
      <OnboardingModal isOpen={showOnboarding} onSkip={handleOnboardingSkip} />

      <main className="px-4 md:px-6 lg:px-8 pb-8 flex flex-col min-h-[calc(100vh-64px)]">
        <div className="max-w-7xl mx-auto w-full flex-1 flex flex-col justify-center">
          {/* Greeting */}
          <div className="flex items-center justify-between mb-8">
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground">
              {greeting}, {displayName.split(' ')[0]} 👋
            </h1>
            <p className="text-xs text-muted-foreground hidden md:block">
              {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>

          {/* Metrics Row */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <DonutMetric
              title="Total Impressions"
              value={metrics?.totalImpressions?.value || '0'}
              change={metrics?.totalImpressions?.change}
              changeType={metrics?.totalImpressions?.changeType}
              color="#A78BFA"
              icon="Eye"
            />
            <DonutMetric
              title="Engagement Rate"
              value={metrics?.engagementRate?.value || '0%'}
              change={metrics?.engagementRate?.change}
              changeType={metrics?.engagementRate?.changeType}
              color="#10B981"
              icon="Heart"
            />
            <DonutMetric
              title="Total Posts"
              value={metrics?.totalPosts?.value || String(recentPosts.length)}
              change={metrics?.totalPosts?.change}
              changeType={metrics?.totalPosts?.changeType}
              color="#3B82F6"
              icon="FileText"
            />
            <DonutMetric
              title="Avg. Reach"
              value={metrics?.avgReach?.value || '0'}
              change={metrics?.avgReach?.change}
              changeType={metrics?.avgReach?.changeType}
              color="#F59E0B"
              icon="Users"
            />
          </div>

          {/* Scheduled Posts Widget */}
          {scheduledPosts.length > 0 && (
            <div className="bg-card border border-border rounded-xl p-5 mb-6">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Icon name="Clock" size={16} className="text-[#A78BFA]" />
                  <h3 className="text-sm font-semibold text-foreground">Upcoming Scheduled</h3>
                  <span className="text-xs bg-[#A78BFA]/20 text-[#A78BFA] px-1.5 py-0.5 rounded-full font-medium">
                    {scheduledPosts.length}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                {scheduledPosts.slice(0, 5).map((post) => {
                  const schedDate = new Date(post.scheduled_at);
                  const platforms = post.platforms || {};
                  const preview = (post.linkedin_content || post.twitter_content || '').slice(0, 60);
                  return (
                    <div key={post.id} className="flex items-center justify-between py-2 px-3 bg-muted/50 rounded-lg group">
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="text-xs text-muted-foreground whitespace-nowrap">
                          {schedDate.toLocaleDateString([], { month: 'short', day: 'numeric' })} at {schedDate.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </div>
                        <div className="flex items-center gap-1">
                          {platforms.linkedin && <Icon name="Linkedin" size={12} className="text-[#0A66C2]" />}
                          {platforms.twitter && <Icon name="X" size={12} className="text-foreground" />}
                        </div>
                        <p className="text-xs text-foreground truncate">{preview}{preview.length >= 60 ? '...' : ''}</p>
                      </div>
                      <button
                        onClick={() => handleCancelScheduled(post.id)}
                        className="text-muted-foreground hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all ml-2 flex-shrink-0"
                        title="Cancel scheduled post"
                      >
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Posts Activity Chart + Recent Posts — FIXED HEIGHT */}
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" style={{ height: '420px' }}>
            {/* Bar Chart — Posts This Week */}
            <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5 flex flex-col h-full">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-semibold text-foreground">Posts This Week</h3>
              </div>
              <div className="flex-1">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={postsByDay} barSize={24}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" vertical={false} />
                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: '#18181B', border: '1px solid #3F3F46', borderRadius: '8px', fontSize: '12px', color: '#FAFAFA' }}
                      labelStyle={{ color: '#FAFAFA' }}
                      itemStyle={{ color: '#A78BFA' }}
                      cursor={{ fill: 'rgba(167,139,250,0.08)' }}
                    />
                    <Bar dataKey="posts" fill="#A78BFA" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Recent Posts — FIXED HEIGHT with scrollable content */}
            <div className="lg:col-span-3 bg-card border border-border rounded-xl flex flex-col h-full">
              {/* Tab header */}
              <div className="flex items-center justify-between px-5 pt-4 pb-0">
                <h3 className="text-sm font-semibold text-foreground">Recent Posts</h3>
                <div className="flex items-center border-b border-border">
                  {PLATFORM_TABS.map(tab => (
                    <button
                      key={tab.key}
                      onClick={() => setActivePlatform(tab.key)}
                      className={`relative flex items-center gap-1.5 px-3 pb-2.5 text-xs font-medium transition-all ${
                        activePlatform === tab.key
                          ? 'text-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {tab.icon && <Icon name={tab.icon} size={13} color={activePlatform === tab.key ? tab.color : undefined} />}
                      {tab.label}
                      {/* Active underline */}
                      {activePlatform === tab.key && (
                        <span className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full" style={{ background: tab.color || '#A78BFA' }} />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Posts list — fixed area, scrolls if needed */}
              <div className="flex-1 overflow-y-auto px-5 py-3">
                {loading ? (
                  <div className="flex items-center justify-center h-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : paginatedPosts.length > 0 ? (
                  <div className="space-y-2">
                    {paginatedPosts.map(post => (
                      <PostRow key={post.id} post={post} onClick={handleViewPost} />
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full">
                    <Icon name="FileText" size={28} className="mb-2 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">
                      {`No ${activePlatform === 'linkedin' ? 'LinkedIn' : 'X'} posts yet.`}
                    </p>
                  </div>
                )}
              </div>

              {/* Pagination — always at bottom */}
              <div className="flex items-center justify-between px-5 py-3 border-t border-border">
                <p className="text-xs text-muted-foreground">
                  {filteredPosts.length > 0
                    ? `${(currentPage - 1) * postsPerPage + 1}–${Math.min(currentPage * postsPerPage, filteredPosts.length)} of ${filteredPosts.length}`
                    : '0 posts'
                  }
                </p>
                {totalPages > 1 && (
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                    >
                      <Icon name="ChevronLeft" size={16} />
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-7 h-7 rounded-lg text-xs font-medium transition-all ${
                          currentPage === page ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                      className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted disabled:opacity-30 transition-colors"
                    >
                      <Icon name="ChevronRight" size={16} />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Dashboard;
